import React from 'react';

import { Route, Routes } from 'react-router-dom';
import LandingRedirect from './LandingRedirect';
import PrivateRoutes from './PrivateRoutes';
import PublicRoutes from './PublicRoutes';
import LoginPage from '../components/Template/LoginTemplate/LoginPage';
import UserProfile from '../components/Template/UserProfileTemplate/UserProfile';
import UsersPage from '../components/Template/UsersTemplate/UsersPage';
import ProgramsPage from '../components/Template/ProgramsTemplate/ProgramsPage';
import SettingsBody from '../components/HOC/SettingsBody/SettingsBody';
import EmailPage from '../components/Template/EmailTemplate/EmailPage';
import NewTemplateEmail from '../components/Template/NewTemplateEmail/NewTemplateEmail';
import GeneralBody from '../components/HOC/GeneralBody/GeneralBody';
import PageNotFound from '../components/Template/PageNotFound/PageNotFound';
import NoPermission from '../components/Template/PageNotFound/NoPermission';
import ActionPage from '../components/Template/ProgramsTemplate/actionPage/ActionPage';
import AcademicCalendarPage from '../components/Template/AcademicCalendarTemplate/AcademicCalendarPage';
import EchoMagazine from '../components/Template/EchoMagazineTemplate/EchoMagazine';
import NewsPage from '../components/Template/NewsAndEventsTemplate/NewsPage';
import UpdateNews from '../components/Template/NewsAndEventsTemplate/actionPage/UpdateNews';
import EventsPage from '../components/Template/NewsAndEventsTemplate/EventsPage';
import NewslettersPage from '../components/Template/NewsAndEventsTemplate/NewslettersPage';
import UpdateNewsletter from '../components/Template/NewsAndEventsTemplate/actionPage/UpdateNewsletter';
import MediaPage from '../components/Template/NewsAndEventsTemplate/MediaPage';
import UpdateEvent from '../components/Template/NewsAndEventsTemplate/actionPage/UpdateEvent';
import MediaGallery from '../components/Template/NewsAndEventsTemplate/MediaGallery';
import SpeechAndTestimonials from '../components/Template/SpeechAndTestimonialsTemplate/SpeechAndTestimonialsPage';
import UpdateSpeech from '../components/Template/SpeechAndTestimonialsTemplate/actionPage/UpdateSpeech';
import CouncilPage from '../components/Template/Council/CouncilPage';
import AlumniPage from '../components/Template/Alumni/AlumniPage';
import UpdateCouncilMember from '../components/Template/Council/actionPage/UpdateCouncilMember';
import UpdateCouncilNews from '../components/Template/Council/actionPage/UpdateCouncilNews';
import UpdateStory from '../components/Template/Alumni/actionPage/UpdateStory';
import UpdateAmbassador from '../components/Template/Alumni/actionPage/UpdateAmbassador';
import UpdateStudent from '../components/Template/Alumni/actionPage/UpdateStudent';
import CreateCouncilCalendar from '../components/Template/Council/actionPage/CreateCouncilCalendar';
import FacultyPage from '../components/Template/Faculty/FacultyPage';
import UpdateFacultyMember from '../components/Template/Faculty/actionPage/UpdateFacultyMember';
import CreateEventCountdown from '../components/Template/NewsAndEventsTemplate/actionPage/CreateEventCountdown';
import ApplicantsPage from '../components/Template/ApplicationsTemplate/ApplicantsPage';
import LeadsPage from '../components/Template/ApplicationsTemplate/LeadsPage';
import ScholarshipsPage from '../components/Template/ApplicationsTemplate/ScholarshipsPage';
import FacilitiesPage from '../components/Template/ApplicationsTemplate/FacilitiesPage';
import ResearchesPage from '../components/Template/ApplicationsTemplate/ResearchesPage';
import QuizzessPage from '../components/Template/ApplicationsTemplate/QuizzessPage';
import HappinessMeter from '../components/Template/ApplicationsTemplate/HappinessMeter';
import ResearchesOpportunities from '../components/Template/ResearchOpportunities/ResearchOpportunities';
import UpdateResearch from '../components/Template/ResearchOpportunities/actionPage/UpdateResearch';
import NewsLetterApplicationPage from '../components/Template/ApplicationsTemplate/NewsLetterApplicationPage';
import HomeSliderPage from '../components/Template/HomeSliderTemplate/HomeSliderPage';
import ContactMessagesPage from '../components/Template/ContactMessagesTemplate/ContactMessagesPage';
import ContactSettingsPage from '../components/Template/ContactSettingsTemplate/ContactSettingsPage';
import CreateHomeSlider from '../components/Template/HomeSliderTemplate/actionPage/CreateHomeSlider';
import StatisticsPage from '../components/Template/statistics/Statistics';
import HomeSettingsPage from '../components/Template/HomeSettingsTemplate/HomeSettingsPage';
import AnnouncementsPage from '../components/Template/AnnouncementsTemplate/AnnouncementsPage';
import AnnouncementForm from '../components/Template/AnnouncementsTemplate/actionPage/AnnouncementForm';
import VisitorCounterPage from '../components/Template/VisitorCounterTemplate/VisitorCounterPage';

/**
 * Routes that exist ONLY here - every other entry in this file is declared
 * inline further down.
 *
 * WHAT USED TO BE HERE. This array was `useState`, each row carried a
 * `hasPermission` flag, and two effects above it fetched
 * `/api/Auth/GetPageAccess` and computed that flag for all 18 rows. The ternary
 * that would have USED the flag was commented out at the bottom of the file, so
 * the result was computed and thrown away on every load - and the other ~57
 * routes were never part of the calculation at all.
 *
 * Enforcement now happens once, in `PageAccessGate` inside `GeneralBody`, which
 * covers every route in this file rather than these 18. The fetch moved to
 * `Context/PageAccessContext`, where the sidebar shares it instead of issuing
 * the same request a second time.
 *
 * `HomeSlider`, `EchoMagazine` and `Guidelines` keep their original casing:
 * those are the spellings the backend serves in `route` (`/home/HomeSlider`),
 * and the hyphenated twins below are what the in-app links use. Both resolve to
 * the same owner in `config/pageAccess.js`, so neither spelling escapes the
 * gate.
 */
const pagesRoutes = [
  { path: 'users', page: <UsersPage /> },
  { path: 'HomeSlider', page: <HomeSliderPage /> },
  // Navigation-menu settings. Still not served by `GetRolePages`, so it cannot
  // be granted per user yet and the sidebar injects it client-side
  // (Molecules/SideBar/SideBar.jsx). Plan:
  // DPA-FE docs/Controlled/MainMenu/04-cms-workplan.md
  { path: 'home-settings', page: <HomeSettingsPage /> },

  // Contact messages from the website's /academics/contact-us form, and the
  // allowed-recipient list behind it. `contact-messages` IS assignable now;
  // `contact-settings` is not, so it stays open until the backend adds it.
  { path: 'contact-messages', page: <ContactMessagesPage /> },
  { path: 'contact-settings', page: <ContactSettingsPage /> },

  // The announcement pop-up and the visitor counter. Both were added to
  // `GetRolePages` by the backend (confirmed 2026-10-21), so both are now
  // granted and enforced like any other screen.
  { path: 'announcements', page: <AnnouncementsPage /> },
  { path: 'visitor-counter', page: <VisitorCounterPage /> },

  { path: 'programs', page: <ProgramsPage /> },
  { path: 'EchoMagazine', page: <EchoMagazine type={0} /> },
  { path: 'Guidelines', page: <EchoMagazine type={1} /> },

  { path: 'applicants', page: <ApplicantsPage /> },
  { path: 'leads', page: <LeadsPage /> },
  { path: 'scholarships', page: <ScholarshipsPage /> },
  { path: 'facilities', page: <FacilitiesPage /> },
  { path: 'research', page: <ResearchesPage /> },
  { path: 'quizzes', page: <QuizzessPage /> },
  { path: 'happinessMeter', page: <HappinessMeter /> },
  { path: 'newslettersubscription', page: <NewsLetterApplicationPage /> },
];

const MainRoutes = () => {
  return (
    <Routes>
      <Route path='/' element={<PublicRoutes />}>
        <Route index element={<LoginPage />} />
      </Route>
      <Route path='/home' element={<PrivateRoutes />}>
        <Route path='' element={<GeneralBody />}>
          {/* `/home` itself. This used to be `<Navigate to='/home' />` - a
              redirect to the route it was already on, so anything that landed
              here looped until the browser gave up. It now resolves to the
              first page the operator actually holds. */}
          <Route index element={<LandingRedirect />} />
          {pagesRoutes.map((item) => (
            <Route key={item.path} path={item.path} element={item.page} />
          ))}
          {/* Shown when an operator holds no pages at all, and when the gate
              refuses a route. Deliberately NOT permissioned - it is the one
              screen a user with nothing granted must still be able to reach. */}
          <Route path='no-permission' element={<NoPermission />} />
          <Route path='/home/profile' element={<UserProfile />} />
          <Route path='/home/home-slider' element={<HomeSliderPage />} />
          <Route path='home-settings' element={<HomeSettingsPage />} />
          <Route path='contact-messages' element={<ContactMessagesPage />} />
          <Route path='contact-settings' element={<ContactSettingsPage />} />
          <Route path='announcements' element={<AnnouncementsPage />} />
          {/* One component for both modes — the presence of `:id` is what
              distinguishes them. A second component would duplicate the
              validation and then drift from it. */}
          <Route path='add-announcement' element={<AnnouncementForm />} />
          <Route path='edit-announcement/:id' element={<AnnouncementForm />} />
          <Route path='visitor-counter' element={<VisitorCounterPage />} />
          <Route path='add-home-slider' element={<CreateHomeSlider />} />
          <Route path='edit-home-slider/:id' element={<CreateHomeSlider />} />
          <Route path='programs/edit/:id' element={<ActionPage />} />
          <Route path='programs/add' element={<ActionPage />} />
          <Route path='attributes' element={<SettingsBody />} />
          <Route path='email' element={<EmailPage />} />
          <Route path='new-email' element={<NewTemplateEmail />} />{' '}
          <Route path='academic-calendar' element={<AcademicCalendarPage />} />
          {/* <Route path="applications" element={<ApplicationsPage />} /> */}
          <Route path='applicants' element={<ApplicantsPage />} />{' '}
          <Route path='leads' element={<LeadsPage />} />{' '}
          <Route
            path='newslettersubscription'
            element={<NewsLetterApplicationPage />}
          />{' '}
          <Route path='scholarships' element={<ScholarshipsPage />} />{' '}
          <Route path='facilities' element={<FacilitiesPage />} />{' '}
          <Route path='research' element={<ResearchesPage />} />{' '}
          <Route path='quizzes' element={<QuizzessPage />} />
          <Route path='echo-magazine' element={<EchoMagazine type={0} />} />
          <Route path='guide-lines' element={<EchoMagazine type={1} />} />
          <Route path='security-and-law' element={<EchoMagazine type={2} />} />
          <Route path='academic-research' element={<EchoMagazine type={3} />} />
          <Route path='news' element={<NewsPage />} />
          <Route path='add-news' element={<UpdateNews />} />
          <Route path='edit-news/:id' element={<UpdateNews />} />
          <Route path='edit-event/:id' element={<UpdateEvent />} />
          <Route path='events/:eventType' element={<EventsPage />} />
          <Route path='add-events' element={<UpdateEvent />} />
          <Route
            path='add-event-countdown'
            element={<CreateEventCountdown />}
          />
          <Route
            path='edit-event-countdown/:id'
            element={<CreateEventCountdown />}
          />
          <Route path='newsletters' element={<NewslettersPage />} />
          <Route path='add-newsletters' element={<UpdateNewsletter />} />
          <Route path='edit-newsletters/:id' element={<UpdateNewsletter />} />
          <Route path='media' element={<MediaPage />} />
          <Route path='media-gallery/:id' element={<MediaGallery />} />
          <Route path='testimonials' element={<SpeechAndTestimonials />} />
          <Route path='council/:active' element={<CouncilPage />} />
          <Route path='faculty' element={<FacultyPage />} />
          <Route path='add-faculty' element={<UpdateFacultyMember />} />
          <Route path='edit-faculty/:id' element={<UpdateFacultyMember />} />
          <Route path='create-speech/:type' element={<UpdateSpeech />} />
          <Route path='update-speech/:type/:id' element={<UpdateSpeech />} />
          <Route
            path='add-council-member/:type'
            element={<UpdateCouncilMember />}
          />
          <Route
            path='add-council-news/:type'
            element={<UpdateCouncilNews />}
          />
          <Route
            path='edit-council-member/:type/:id'
            element={<UpdateCouncilMember />}
          />
          <Route
            path='edit-council-news/:type/:id'
            element={<UpdateCouncilNews />}
          />
          <Route
            path='create-council-calendar'
            element={<CreateCouncilCalendar />}
          />
          <Route
            path='edit-council-calendar/:id'
            element={<CreateCouncilCalendar />}
          />
          <Route path='add-student-alumni' element={<UpdateStudent />} />
          <Route path='add-success-story' element={<UpdateStory />} />
          <Route
            path='research-opportunities'
            element={<ResearchesOpportunities />}
          />
          <Route path='add-ambassador' element={<UpdateAmbassador />} />
          <Route path='edit-success-story/:id' element={<UpdateStory />} />
          <Route path='edit-ambassador/:id' element={<UpdateAmbassador />} />
          <Route path='edit-student-alumni/:id' element={<UpdateStudent />} />
          <Route path='alumni/:active' element={<AlumniPage />} />
          <Route
            path='create-research-opportunity'
            element={<UpdateResearch />}
          />
          <Route
            path='edit-research-opportunity/:id'
            element={<UpdateResearch />}
          />
          <Route path='/home/statistics' element={<StatisticsPage />} />
          <Route path='*' element={<PageNotFound />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default MainRoutes;
